import { AlertTriangle, Bell, CheckCircle, Info, X, XCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast, { Toaster as HotToaster, Toast } from 'react-hot-toast';
export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'notification';

const CONFIG: Record<
  ToastType,
  {
    icon: React.ReactNode;
    bar: string;
    bg: string;
    border: string;
    iconColor: string;
  }
> = {
  success: {
    icon: <CheckCircle size={16} />,
    bar: 'bg-emerald-500',
    bg: 'bg-[#0d1f17]',
    border: 'border-emerald-500/20',
    iconColor: 'text-emerald-400',
  },
  error: {
    icon: <XCircle size={16} />,
    bar: 'bg-red-500',
    bg: 'bg-[#1f0d0d]',
    border: 'border-red-500/20',
    iconColor: 'text-red-400',
  },
  info: {
    icon: <Info size={16} />,
    bar: 'bg-blue-500',
    bg: 'bg-[#0d1420]',
    border: 'border-blue-500/20',
    iconColor: 'text-blue-400',
  },
  warning: {
    icon: <AlertTriangle size={16} />,
    bar: 'bg-amber-500',
    bg: 'bg-[#1f1a0d]',
    border: 'border-amber-500/20',
    iconColor: 'text-amber-400',
  },
  notification: {
    icon: <Bell size={16} />,
    bar: 'bg-purple-500',
    bg: 'bg-[#120d1f]',
    border: 'border-purple-500/20',
    iconColor: 'text-purple-400',
  },
};

interface CustomToastProps {
  t: Toast;
  type: ToastType;
  message: string;
  title?: string;
}

const CustomToast = ({ t, type, message, title }: CustomToastProps) => {
  const cfg = CONFIG[type];

  return (
    <motion.div
      initial={{ opacity: 0, y: -12, scale: 0.95, filter: 'blur(4px)' }}
      animate={
        t.visible
          ? { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }
          : { opacity: 0, y: -8, scale: 0.96, filter: 'blur(2px)' }
      }
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
      className={`
        relative flex items-start gap-3 overflow-hidden
        w-[320px] rounded-2xl border px-4 py-3.5
        ${cfg.bg} ${cfg.border}
        shadow-[0_8px_32px_rgba(0,0,0,0.4)]
      `}
    >
      {/* left accent bar */}
      <div className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-full ${cfg.bar}`} />

      {/* icon */}
      <div className={`mt-0.5 shrink-0 ${cfg.iconColor}`}>{cfg.icon}</div>

      {/* content */}
      <div className="flex-1 min-w-0">
        {title && (
          <p className="text-[13px] font-semibold text-white mb-0.5 leading-snug">{title}</p>
        )}
        <p className={`text-[12px] leading-[1.5] ${title ? 'text-white/60' : 'text-white/80'}`}>
          {message}
        </p>
      </div>

      {/* close */}
      <button
        onClick={() => toast.dismiss(t.id)}
        className="shrink-0 mt-0.5 text-white/30 hover:text-white/70 transition-colors"
      >
        <X size={13} />
      </button>

      {/* progress bar */}
      <motion.div
        initial={{ scaleX: 1 }}
        animate={{ scaleX: 0 }}
        transition={{ duration: (t.duration ?? 3000) / 1000, ease: 'linear' }}
        style={{ originX: 0 }}
        className={`absolute bottom-0 left-0 right-0 h-[2px] ${cfg.bar} opacity-30`}
      />
    </motion.div>
  );
};

export function Toaster() {
  return (
    <HotToaster position="top-center" gutter={8} toastOptions={{ duration: 3000 }}>
      {(t) => {
        const { type, message, title } = t.message as any;
        return <CustomToast t={t} type={type} message={message} title={title} />;
      }}
    </HotToaster>
  );
}
